import { type FC, type PropsWithChildren, type ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import LightningIcon from 'assets/svg/app/lightning.svg'
import { setPreferences } from 'state/futures/reducer'
import { selectIsSponsoredProvider } from 'state/futures/selectors'
import { TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import media from 'styles/media'

export const SuggestedBadge: FC<{ outlined?: boolean }> = ({ outlined = false }) => {
	const { t } = useTranslation()
	const isSponsoredProvider = useAppSelector(selectIsSponsoredProvider)

	return (
		<Badge outlined={outlined}>
			<span>
				{isSponsoredProvider
					? t('futures.modals.trading-mode.recommended')
					: t('futures.modals.trading-mode.suggested')}
			</span>
			<StyledLightningIcon width={6} height={8} />
		</Badge>
	)
}

type SelectTradingTypeProps = {
	isCompact?: boolean
}

const SelectTradingType: FC<SelectTradingTypeProps> = ({ isCompact }) => {
	const { t } = useTranslation()
	const [option, setOption] = useState(TradingModes.ONE_CLICK)
	const dispatch = useAppDispatch()

	const changeTradingMode = useCallback(
		(mode: TradingModes) => {
			setOption(mode)
			dispatch(setPreferences({ tradingMode: mode }))
		},
		[dispatch]
	)

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
			<TradingRadioItem
				title={t('futures.modals.trading-mode.one-click-title')}
				description={t('futures.modals.trading-mode.one-click-description')}
				isActive={option === TradingModes.ONE_CLICK}
				isCompact={isCompact}
				onClick={() => changeTradingMode(TradingModes.ONE_CLICK)}
				label={<SuggestedBadge />}
			/>
			<TradingRadioItem
				title={t('futures.modals.trading-mode.default-title')}
				description={t('futures.modals.trading-mode.default-description')}
				isActive={option === TradingModes.DEFAULT}
				isCompact={isCompact}
				onClick={() => changeTradingMode(TradingModes.DEFAULT)}
			/>
		</div>
	)
}

export default SelectTradingType

type TradingRadioItemProps = PropsWithChildren<{
	title: string
	description?: string
	label?: ReactElement
	isActive: boolean
	isCompact?: boolean
	onClick: VoidFunction
}>

const TradingRadioItem: FC<TradingRadioItemProps> = ({
	title,
	description,
	label,
	isActive,
	onClick,
	isCompact,
	children,
}) => {
	return (
		<RadioItem onClick={onClick} isActive={isActive}>
			<Row style={!isCompact ? { marginBottom: '15px' } : {}}>
				<Centered>
					<RadioTitle>{title}</RadioTitle>
					{!!label && label}
				</Centered>
				<RadioIndicator isActive={isActive} />
			</Row>
			{!isCompact && (
				<>
					{description && <div style={{ width: '100%', textAlign: 'left' }}>{description}</div>}
					{children}
				</>
			)}
		</RadioItem>
	)
}

const RadioItem = styled.button<{ isActive: boolean }>`
	padding: 15px;
	background-color: transparent;
	border-radius: 4px;
	cursor: pointer;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	font-size: 12px;

	${media.greaterThan('md')`
		min-width: 400px;
	`}

	${(props) => css`
		border: ${props.theme.colors.selectedTheme.newTheme.button.default.border};
		border-color: ${
			props.isActive ? props.theme.colors.selectedTheme.newTheme.badge.yellow.background : null
		};
	`}
`

const RadioTitle = styled.b`
	font-size: 15px;
	font-weight: 700;
	line-height: 1;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.button.default.color};
	margin-right: 10px;
`

const RadioIndicator = styled.span<{ isActive: boolean }>`
	border-radius: 50%;
	width: 18px;
	height: 18px;
	border: 4px solid
		${(props) =>
			props.isActive
				? props.theme.colors.selectedTheme.newTheme.badge.yellow.background
				: props.theme.colors.selectedTheme.newTheme.button.default.borderColor};
	background-color: ${(props) => (props.isActive ? '#ffffff' : 'transparent')};
`

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`

const Centered = styled.div`
	display: flex;
	align-items: center;
`

const Badge = styled.span<{ outlined?: boolean }>`
	display: flex;
	align-items: center;
	gap: 5px;

	padding: 3px 6px;
	color: ${(props) =>
		props.outlined
			? props.theme.colors.selectedTheme.newTheme.button.default.color
			: props.theme.colors.common.darkYellow};
	border-radius: 100px;
	font-size: 9px;
	// ${(props) => !props.outlined && 'font-weight: 600;'}
	line-height: 1.2;
	${(props) =>
		props.outlined
			? `
			border: 1px solid ${props.theme.colors.selectedTheme.newTheme.badge.yellow.background};
			font-size: 10px;
			`
			: `
			background-color: ${props.theme.colors.selectedTheme.newTheme.badge.yellow.background};
			font-size: 9px;
			font-weight: 600;
			font-feature-settings: 'tnum';
			`}
`

const StyledLightningIcon = styled(LightningIcon)`
	path {
		fill: ${(props) => props.theme.colors.common.darkYellow};
	}
`
